import type { ArticleNeighbourResponse } from '@shared/api-utils';
import { ChevronLeft, ChevronRight } from '@shared/icons';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { Link } from '../../Link/Link';
import { cn } from '../../shadcn';

const CARD_BASE_CLASSES = 'flex min-w-0 flex-1 items-center gap-2 rounded-sm border border-border bg-container p-4 md:w-44 md:flex-none paragraph-s-strong';

const LINK_CARD_CLASSES = cn(
  CARD_BASE_CLASSES,
  'justify-start whitespace-normal text-foreground no-underline transition-colors hover:bg-container-hover',
  'focus-visible:border-ring focus-visible:ring-foreground focus-visible:ring-[3px]'
);

const DISABLED_CARD_CLASSES = cn(CARD_BASE_CLASSES, 'opacity-40');

type ArticlePaginationProps = {
  previousArticle?: ArticleNeighbourResponse | null;
  nextArticle?: ArticleNeighbourResponse | null;
  className?: string;
  'data-testid'?: string;
};

function ArticlePaginationCardContent({ direction, title, 'data-testid': dataTestId }: { direction: 'previous' | 'next'; title: string; 'data-testid': string }) {
  const chevron =
    direction === 'previous' ? (
      <ChevronLeft size="medium" className="shrink-0" aria-hidden="true" data-testid={`${dataTestId}-chevron`} />
    ) : (
      <ChevronRight size="medium" className="shrink-0" aria-hidden="true" data-testid={`${dataTestId}-chevron`} />
    );

  return (
    <>
      {direction === 'previous' && chevron}
      <span className="min-w-0 flex-1 break-words line-clamp-3 xs:line-clamp-2 text-left" data-testid={`${dataTestId}-title`}>
        {title}
      </span>
      {direction === 'next' && chevron}
    </>
  );
}

function ArticlePaginationCard({
  article,
  direction,
  disabledLabel,
  'data-testid': dataTestId,
}: {
  article?: ArticleNeighbourResponse | null;
  direction: 'previous' | 'next';
  disabledLabel: string;
  'data-testid': string;
}) {
  const content = <ArticlePaginationCardContent direction={direction} title={article?.title ?? disabledLabel} data-testid={dataTestId} />;

  if (!article) {
    return (
      <div className={DISABLED_CARD_CLASSES} data-testid={dataTestId}>
        {content}
      </div>
    );
  }

  return (
    <Link
      href={ROUTES.MAGAZINE.ARTICLE.replace(':articleSlug', article.slug)}
      simplified
      className={LINK_CARD_CLASSES}
      data-track-name={direction === 'previous' ? 'Previous' : 'Next'}
      data-testid={dataTestId}
    >
      {content}
    </Link>
  );
}

function ArticlePagination({ previousArticle, nextArticle, className, 'data-testid': dataTestId = 'article-pagination' }: ArticlePaginationProps) {
  const t = useTranslations('magazine.article_pagination');
  const disabledLabel = t('no_more_articles');

  return (
    <nav aria-label={t('nav_label')} className={cn('flex w-full items-stretch justify-between gap-2', className)} data-track-container="Pagination" data-testid={dataTestId}>
      <ArticlePaginationCard article={previousArticle} direction="previous" disabledLabel={disabledLabel} data-testid={`${dataTestId}-previous`} />
      <ArticlePaginationCard article={nextArticle} direction="next" disabledLabel={disabledLabel} data-testid={`${dataTestId}-next`} />
    </nav>
  );
}

export { ArticlePagination, type ArticlePaginationProps };
